<script setup>
import Button from '@/components/ui/button/Button.vue';
import GenericCard from '@/components/ui/card/GenericCard.vue';
import Separator from '@/components/ui/separator/Separator.vue';
import { useLocalization, useConfig } from '@/composables';
import { computed } from 'vue';

const { numberToMoney } = useLocalization();
const { standardSubscriptionMonthlyPrice, standardSubscriptionAnnualPrice, enterpriseSubscriptionMonthlyPrice } = useConfig();

const props = defineProps({
    setupData: {
        type: Object,
        required: true
    },
    isStandardPlan: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['change-plan', 'change-contact-info', 'change-hubs']);

const subscriptionPlan = computed(() => {
    return props.setupData?.subscription_plan;
})

const subscriptionPrice = computed(() => {
    if (subscriptionPlan.value?.name === 'standard') {
        return subscriptionPlan.value?.yearly ? standardSubscriptionAnnualPrice : standardSubscriptionMonthlyPrice;
    } else {
        return subscriptionPlan.value?.yearly ? 0 : enterpriseSubscriptionMonthlyPrice;
    }
});

const phoneNumber = computed(() => {
    // Append 0 if the phone number doesn't start with 0
    if (props.setupData?.phone.startsWith('0')) {
        return props.setupData?.phone;
    }
    return '0' + props.setupData?.phone;
});

const ownerName = computed(() => {
    return props.setupData?.first_name + ' ' + props.setupData?.last_name;
});

const hubs = computed(() => {
    return props.setupData?.hubs;
});

const changePlan = () => {
    emit('change-plan');
}

const changeContactInfo = () => {
    emit('change-contact-info');
}

const changeHubs = () => {
    console.log('change-hubs');
    emit('change-hubs');
}

</script>

<template>
    <GenericCard content-class="pt-6">
        <template #content>
            <div class="flex flex-col justify-start items-start gap-2">
                <div class="text-xl font-medium text-primary-font">
                    Your Setup
                </div>
                <div class="text-sm text-primary-gray">
                    Selected Plan
                </div>
                <div class="w-full flex justify-between items-center gap-2">
                    <span class="text-base text-primary-font font-medium capitalize">
                        {{ subscriptionPlan?.name ?? '' }}
                    </span>
                    <Button variant="ghost" size="sm" class="text-primary-blue text-sm font-medium" @click="changePlan">
                        Change
                    </Button>
                </div>
                <div class="text-primary-green">
                    <span class="text-2xl font-medium">
                        {{ numberToMoney(subscriptionPrice, '', 0) }}
                    </span>
                    <span class="text-base">
                        / {{ subscriptionPlan?.yearly ? 'year' : 'month' }}
                    </span>
                </div>
            </div>
            <Separator class="my-6 bg-primary-border" />
            <div class="flex flex-col justify-start items-start gap-4">
                <div class="w-full flex justify-between items-center gap-2">
                    <span class="text-sm text-primary-gray">
                        Contact Information
                    </span>
                    <Button variant="ghost" size="sm" class="text-primary-blue text-sm font-medium" @click="changeContactInfo">
                        Change
                    </Button>
                </div>
                <div class="w-full flex flex-col justify-start items-start gap-2">
                    <div class="w-full flex justify-between items-center gap-2">
                        <span class="text-sm text-primary-gray">
                            Phone Number
                        </span>
                        <span class="text-sm text-primary-font">
                            {{ phoneNumber ?? '' }}
                        </span>
                    </div>
                    <div class="w-full flex justify-between items-center gap-2">
                        <span class="text-sm text-primary-gray">
                            Name
                        </span>
                        <span class="text-sm text-primary-font">
                            {{ ownerName ?? '' }}
                        </span>
                    </div>
                    <div class="w-full flex justify-between items-center gap-2">
                        <span class="text-sm text-primary-gray">
                            Email
                        </span>
                        <span class="text-sm text-primary-font">
                            {{ setupData?.email ?? '' }}
                        </span>
                    </div>
                </div>
            </div>
            <Separator class="my-6 bg-primary-border" />
            <div v-if="isStandardPlan" class="flex flex-col justify-start items-start gap-2">
                <div class="w-full flex justify-between items-center gap-2">
                    <span class="text-sm text-primary-gray">
                        Market
                    </span>
                </div>
                <div class="w-full flex flex-col justify-start items-start gap-2">
                    <div class="w-full flex justify-between items-center gap-2">
                        <span class="text-base font-medium text-primary-gray">
                            {{ setupData?.organization_name }}
                        </span>
                        <Button variant="ghost" size="sm" class="text-primary-blue text-sm font-medium" @click="changeHubs">
                            Change
                        </Button>
                    </div>
                </div>
            </div>
            <div v-else class="flex flex-col justify-start items-start gap-2">
                <div class="w-full flex justify-between items-center gap-2">
                    <span class="text-sm text-primary-gray">
                        Markets ({{ hubs.length }})
                    </span>
                </div>
                <div class="w-full flex flex-col justify-start items-start gap-2">
                    <div v-for="hub in hubs" :key="hub.name" class="w-full flex justify-between items-center gap-2">
                        <span class="text-base font-medium text-primary-gray">
                            {{ hub.name }}
                        </span>
                        <Button variant="ghost" size="sm" class="text-primary-blue text-sm font-medium" @click="changeHubs">
                            Change
                        </Button>
                    </div>
                </div>
            </div>
        </template>
    </GenericCard>
</template>